import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

const KEYS_NOT_TO_SEND_TO_EXPO = [
  '_hasHydrated',
  'userData',
  'newUser',
  'firstOpen',
];

const sendMessageToExpoApp = (type, key, value) => {
  if (window.ReactNativeWebView && !KEYS_NOT_TO_SEND_TO_EXPO.includes(key)) {
    const message = JSON.stringify({ type, key, value });
    window.ReactNativeWebView.postMessage(message);
  }
};

export const useLocalStorageStore = create(
  persist(
    (set, get) => {
      const wrappedSet = (partial, replace, actionKey) => {
        set(partial, replace);
        if (actionKey) {
          const newState = get();
          const value = newState[actionKey];
          sendMessageToExpoApp('USER_SETTING', actionKey, value);
        }
      };

      return {
        // User Data
        userData: {},
        updateUserData: (data) =>
          wrappedSet(
            (state) => ({ userData: { ...state.userData, ...data } }),
            false,
            'userData'
          ),
        setUserData: (data) =>
          wrappedSet({ userData: data }, false, 'userData'),
        clearUserData: () => wrappedSet({ userData: {} }, false, 'userData'),

        // Cache and Version
        lastCacheClearTime: null,
        setLastCacheClearTime: (time) =>
          wrappedSet({ lastCacheClearTime: time }, false, 'lastCacheClearTime'),
        appVersion: null,
        setAppVersion: (version) =>
          wrappedSet({ appVersion: version }, false, 'appVersion'),

        // Duel/Match Game
        questionData: [],
        setQuestionData: (data) =>
          wrappedSet({ questionData: data }, false, 'questionData'),
        clearQuestionData: () =>
          wrappedSet({ questionData: [] }, false, 'questionData'),
        userCurrentQuestion: null,
        setUserCurrentQuestion: (index) =>
          wrappedSet(
            { userCurrentQuestion: index },
            false,
            'userCurrentQuestion'
          ),
        clearUserCurrentQuestion: () =>
          wrappedSet(
            { userCurrentQuestion: null },
            false,
            'userCurrentQuestion'
          ),
        duelActiveTab: 'duelOnline',
        setDuelActiveTab: (tab) =>
          wrappedSet({ duelActiveTab: tab }, false, 'duelActiveTab'),
        duelInviteData: null,
        setDuelInviteData: (data) =>
          wrappedSet({ duelInviteData: data }, false, 'duelInviteData'),
        clearDuelInviteData: () =>
          wrappedSet({ duelInviteData: null }, false, 'duelInviteData'),

        // Practice Questions
        practiceQuestionStates: [],
        setPracticeQuestionStates: (states) =>
          wrappedSet(
            { practiceQuestionStates: states },
            false,
            'practiceQuestionStates'
          ),
        practiceCurrentIndex: 0,
        setPracticeCurrentIndex: (index) =>
          wrappedSet(
            { practiceCurrentIndex: index },
            false,
            'practiceCurrentIndex'
          ),
        practiceQuestions: [],
        setPracticeQuestions: (questions) =>
          wrappedSet(
            { practiceQuestions: questions },
            false,
            'practiceQuestions'
          ),
        clearPracticeData: () =>
          wrappedSet(
            {
              practiceQuestionStates: [],
              practiceCurrentIndex: 0,
              practiceQuestions: [],
            },
            false,
            'practiceQuestions'
          ),
        practiceActiveTab: 0,
        setPracticeActiveTab: (id) =>
          wrappedSet({ practiceActiveTab: id }, false, 'practiceActiveTab'),

        // News
        newsListScrollPosition: 0,
        setNewsListScrollPosition: (position) =>
          wrappedSet(
            { newsListScrollPosition: position },
            false,
            'newsListScrollPosition'
          ),
        newsDetailScrollPositions: {},
        setNewsDetailScrollPosition: (newsId, position) =>
          wrappedSet(
            (state) => ({
              newsDetailScrollPositions: {
                ...state.newsDetailScrollPositions,
                [newsId]: position,
              },
            }),
            false,
            'newsDetailScrollPositions'
          ),
        newsDetailScrollTimestamps: {},
        setNewsDetailScrollTimestamp: (newsId, timestamp) =>
          wrappedSet(
            (state) => ({
              newsDetailScrollTimestamps: {
                ...state.newsDetailScrollTimestamps,
                [newsId]: timestamp,
              },
            }),
            false,
            'newsDetailScrollTimestamps'
          ),
        clearNewsDetailScrollPosition: (newsId) =>
          wrappedSet(
            (state) => {
              const newPositions = { ...state.newsDetailScrollPositions };
              delete newPositions[newsId];
              const newTimestamps = { ...state.newsDetailScrollTimestamps };
              delete newTimestamps[newsId];
              return {
                newsDetailScrollPositions: newPositions,
                newsDetailScrollTimestamps: newTimestamps,
              };
            },
            false,
            'newsDetailScrollPositions'
          ),
        newsActiveTab: 'Latest News',
        setNewsActiveTab: (tab) =>
          wrappedSet({ newsActiveTab: tab }, false, 'newsActiveTab'),
        newsActiveTabIndex: '0',
        setNewsActiveTabIndex: (index) =>
          wrappedSet(
            { newsActiveTabIndex: index },
            false,
            'newsActiveTabIndex'
          ),

        // User Behavior / Flags
        newUser: null,
        setNewUser: (isNew) => wrappedSet({ newUser: isNew }, false, 'newUser'),
        seenVideos: {},
        markVideoAsSeen: (feature) =>
          wrappedSet(
            (state) => ({
              seenVideos: { ...state.seenVideos, [feature]: true },
            }),
            false,
            'seenVideos'
          ),
        lastVideoTime: 0,
        setLastVideoTime: (time) =>
          wrappedSet({ lastVideoTime: time }, false, 'lastVideoTime'),
        previousStreak: 0,
        setPreviousStreak: (streak) =>
          wrappedSet({ previousStreak: streak }, false, 'previousStreak'),
        streakDate: null,
        setStreakDate: (date) =>
          wrappedSet({ streakDate: date }, false, 'streakDate'),
        lastSparkModalShown: null,
        setLastSparkModalShown: (time) =>
          wrappedSet(
            { lastSparkModalShown: time },
            false,
            'lastSparkModalShown'
          ),
        isDismissed: null,
        setIsDismissed: (dismissed) =>
          wrappedSet({ isDismissed: dismissed }, false, 'isDismissed'),
        firstOpen: null,
        setFirstOpen: (status) =>
          wrappedSet({ firstOpen: status }, false, 'firstOpen'),
        isSignupNewUser: null,
        setIsSignupNewUser: (status) =>
          wrappedSet({ isSignupNewUser: status }, false, 'isSignupNewUser'),
        completedOnboarding: null,
        setCompletedOnboarding: (status) =>
          wrappedSet(
            { completedOnboarding: status },
            false,
            'completedOnboarding'
          ),
        clearCompletedOnboarding: () =>
          wrappedSet(
            { completedOnboarding: null },
            false,
            'completedOnboarding'
          ),
        postLoginRedirect: null,
        setPostLoginRedirect: (path) =>
          wrappedSet({ postLoginRedirect: path }, false, 'postLoginRedirect'),
        clearPostLoginRedirect: () =>
          wrappedSet({ postLoginRedirect: null }, false, 'postLoginRedirect'),

        // MoEngage
        moeData: {},
        setMoeData: (data) => wrappedSet({ moeData: data }, false, 'moeData'),

        // PWA and Navigation
        pwaInstallDeeplink: {},
        setPwaInstallDeeplink: (data) =>
          wrappedSet({ pwaInstallDeeplink: data }, false, 'pwaInstallDeeplink'),
        clearPwaInstallDeeplink: () =>
          wrappedSet({ pwaInstallDeeplink: {} }, false, 'pwaInstallDeeplink'),
        intendedDestination: {},
        setIntendedDestination: (destination) =>
          wrappedSet(
            { intendedDestination: destination },
            false,
            'intendedDestination'
          ),
        clearIntendedDestination: () =>
          wrappedSet({ intendedDestination: {} }, false, 'intendedDestination'),
        branchData: {},
        setBranchData: (data) =>
          wrappedSet({ branchData: data }, false, 'branchData'),
        clearBranchData: () =>
          wrappedSet({ branchData: {} }, false, 'branchData'),

        // Info Highlight Modal
        infoHighlightModalShown: false,
        setInfoHighlightModalShown: (shown) =>
          wrappedSet(
            { infoHighlightModalShown: shown },
            false,
            'infoHighlightModalShown'
          ),

        // Additional items from AuthUtils.js & others
        storedUserId: null,
        setStoredUserId: (id) =>
          wrappedSet({ storedUserId: id }, false, 'storedUserId'),
        phoneNumber: null,
        setPhoneNumber: (number) =>
          wrappedSet({ phoneNumber: number }, false, 'phoneNumber'),
        clearPhoneNumber: () =>
          wrappedSet({ phoneNumber: null }, false, 'phoneNumber'),

        // Theme
        theme: 'light',
        setTheme: (themeName) =>
          wrappedSet({ theme: themeName }, false, 'theme'),
        clearTheme: () => wrappedSet({ theme: 'light' }, false, 'theme'),

        // Chat specific
        chatActiveTab: 0,
        setChatActiveTab: (index) =>
          wrappedSet({ chatActiveTab: index }, false, 'chatActiveTab'),
        hasVisitedChatScreen: null,
        setHasVisitedChatScreen: (status) =>
          wrappedSet(
            { hasVisitedChatScreen: status },
            false,
            'hasVisitedChatScreen'
          ),
        firstVisitChat: null,
        setFirstVisitChat: (status) =>
          wrappedSet({ firstVisitChat: status }, false, 'firstVisitChat'),

        // Phone Verification Modal
        phoneVerifyModalShownTimestamp: null,
        setPhoneVerifyModalShownTimestamp: (timestamp) =>
          wrappedSet(
            { phoneVerifyModalShownTimestamp: timestamp },
            false,
            'phoneVerifyModalShownTimestamp'
          ),

        // Rating Modal
        lastPopupTime: null,
        setLastPopupTime: (time) =>
          wrappedSet({ lastPopupTime: time }, false, 'lastPopupTime'),
        hasRated: null,
        setHasRated: (status) =>
          wrappedSet({ hasRated: status }, false, 'hasRated'),
        lastCloseTimeRatingModal: null,
        setLastCloseTimeRatingModal: (time) =>
          wrappedSet(
            { lastCloseTimeRatingModal: time },
            false,
            'lastCloseTimeRatingModal'
          ),

        // Function to remove a specific item (imitating localStorage.removeItem)
        removeItem: (key) => {
          switch (key) {
            case 'userData':
              get().clearUserData();
              break;
            case 'appVersion':
              wrappedSet({ appVersion: null }, false, 'appVersion');
              break;
            case 'lastCacheClearTime':
              wrappedSet(
                { lastCacheClearTime: null },
                false,
                'lastCacheClearTime'
              );
              break;
            case 'questionData':
              get().clearQuestionData();
              break;
            case 'userCurrentQuestion':
              get().clearUserCurrentQuestion();
              break;
            case 'practiceQuestionStates':
            case 'practiceCurrentIndex':
            case 'practiceQuestions':
              get().clearPracticeData();
              break;
            case 'newsListScrollPosition':
              wrappedSet(
                { newsListScrollPosition: 0 },
                false,
                'newsListScrollPosition'
              );
              break;
            case 'newUser':
              wrappedSet({ newUser: null }, false, 'newUser');
              break;
            case 'seenVideos':
              wrappedSet({ seenVideos: {} }, false, 'seenVideos');
              break;
            case 'lastVideoTime':
              wrappedSet({ lastVideoTime: 0 }, false, 'lastVideoTime');
              break;
            case 'previousStreak':
              wrappedSet({ previousStreak: 0 }, false, 'previousStreak');
              break;
            case 'streakDate':
              wrappedSet({ streakDate: null }, false, 'streakDate');
              break;
            case 'lastSparkModalShown':
              wrappedSet(
                { lastSparkModalShown: null },
                false,
                'lastSparkModalShown'
              );
              break;
            case 'isDismissed':
              wrappedSet({ isDismissed: null }, false, 'isDismissed');
              break;
            case 'firstOpen':
              wrappedSet({ firstOpen: null }, false, 'firstOpen');
              break;
            case 'isSignupNewUser':
              wrappedSet({ isSignupNewUser: null }, false, 'isSignupNewUser');
              break;
            case 'moeData':
              wrappedSet({ moeData: {} }, false, 'moeData');
              break;
            case 'pwaInstallDeeplink':
              get().clearPwaInstallDeeplink();
              break;
            case 'intendedDestination':
              get().clearIntendedDestination();
              break;
            case 'branchData':
              get().clearBranchData();
              break;
            case 'infoHighlightModalShown':
              wrappedSet(
                { infoHighlightModalShown: false },
                false,
                'infoHighlightModalShown'
              );
              break;
            case 'storedUserId':
              wrappedSet({ storedUserId: null }, false, 'storedUserId');
              break;
            case 'phoneNumber':
              get().clearPhoneNumber();
              break;
            case 'theme':
              get().clearTheme();
              break;
            case 'completedOnboarding':
              get().clearCompletedOnboarding();
              break;
            case 'postLoginRedirect':
              get().clearPostLoginRedirect();
              break;
            case 'duelActiveTab':
              wrappedSet(
                { duelActiveTab: 'duelOnline' },
                false,
                'duelActiveTab'
              );
              break;
            case 'duelInviteData':
              get().clearDuelInviteData();
              break;
            case 'practiceActiveTab':
              wrappedSet({ practiceActiveTab: 0 }, false, 'practiceActiveTab');
              break;
            case 'newsActiveTab':
              wrappedSet(
                { newsActiveTab: 'Latest News' },
                false,
                'newsActiveTab'
              );
              break;
            case 'newsActiveTabIndex':
              wrappedSet(
                { newsActiveTabIndex: '0' },
                false,
                'newsActiveTabIndex'
              );
              break;
            case 'chatActiveTab':
              wrappedSet({ chatActiveTab: 0 }, false, 'chatActiveTab');
              break;
            case 'hasVisitedChatScreen':
              wrappedSet(
                { hasVisitedChatScreen: null },
                false,
                'hasVisitedChatScreen'
              );
              break;
            case 'firstVisitChat':
              wrappedSet({ firstVisitChat: null }, false, 'firstVisitChat');
              break;
            case 'phoneVerifyModalShownTimestamp':
              wrappedSet(
                { phoneVerifyModalShownTimestamp: null },
                false,
                'phoneVerifyModalShownTimestamp'
              );
              break;
            case 'lastPopupTime':
              wrappedSet({ lastPopupTime: null }, false, 'lastPopupTime');
              break;
            case 'hasRated':
              wrappedSet({ hasRated: null }, false, 'hasRated');
              break;
            case 'lastCloseTimeRatingModal':
              wrappedSet(
                { lastCloseTimeRatingModal: null },
                false,
                'lastCloseTimeRatingModal'
              );
              break;
            default:
              wrappedSet({ [key]: undefined }, false, key);
          }
        },

        // Function to clear multiple items (delegates to individual removers)
        clearItems: (keys) => {
          keys.forEach((key) => get().removeItem(key));
        },

        // Full clear, similar to localStorage.clear() but for the store
        clearAllStoreData: () => {
          // Call all individual clear/reset methods or set directly to initial state
          get().clearUserData();
          wrappedSet(
            { lastCacheClearTime: null, appVersion: null },
            false,
            'appVersion'
          );
          get().clearQuestionData();
          get().clearUserCurrentQuestion();
          get().clearPracticeData();
          wrappedSet(
            {
              newsListScrollPosition: 0,
              newsDetailScrollPositions: {},
              newsDetailScrollTimestamps: {},
            },
            false,
            'newsListScrollPositions'
          );
          wrappedSet(
            { newsActiveTab: 'Latest News', newsActiveTabIndex: '0' },
            false,
            'newsActiveTab'
          );
          wrappedSet(
            {
              newUser: null,
              seenVideos: {},
              lastVideoTime: 0,
              previousStreak: 0,
              streakDate: null,
            },
            false,
            'newUser'
          );
          wrappedSet(
            {
              lastSparkModalShown: null,
              isDismissed: null,
              firstOpen: null,
              isSignupNewUser: null,
            },
            false,
            'lastSparkModalShown'
          );
          get().clearCompletedOnboarding();
          get().clearPostLoginRedirect();
          wrappedSet({ moeData: {} }, false, 'moeData');
          get().clearPwaInstallDeeplink();
          get().clearIntendedDestination();
          get().clearBranchData();
          wrappedSet(
            { infoHighlightModalShown: false },
            false,
            'infoHighlightModalShown'
          );
          wrappedSet({ storedUserId: null }, false, 'storedUserId');
          get().clearPhoneNumber();
          get().clearTheme();
          wrappedSet({ duelActiveTab: 'duelOnline' }, false, 'duelActiveTab');
          get().clearDuelInviteData();
          wrappedSet({ practiceActiveTab: 0 }, false, 'practiceActiveTab');
          wrappedSet(
            {
              chatActiveTab: 0,
              hasVisitedChatScreen: null,
              firstVisitChat: null,
            },
            false,
            'chatActiveTab'
          );
          wrappedSet(
            { phoneVerifyModalShownTimestamp: null },
            false,
            'phoneVerifyModalShownTimestamp'
          );
          wrappedSet(
            {
              lastPopupTime: null,
              hasRated: null,
              lastCloseTimeRatingModal: null,
            },
            false,
            'lastPopupTime'
          );
          wrappedSet(
            { _hasHydrated: get()._hasHydrated },
            false,
            '_hasHydrated'
          );
        },

        _hasHydrated: false,
        setHasHydrated: (hydrated) =>
          wrappedSet({ _hasHydrated: hydrated }, false, '_hasHydrated'),
      };
    },
    {
      name: 'padhai-local-storage',
      storage: createJSONStorage(() => localStorage),
      version: 0,
      onRehydrateStorage: () => (state) => {
        if (state && typeof state.setHasHydrated === 'function') {
          state.setHasHydrated(true);
          // console.log('[localStorageStore] Hydration complete.');
        } else {
          console.warn(
            '[localStorageStore] setHasHydrated not available directly in onRehydrateStorage. Attempting direct set.'
          );
          useLocalStorageStore.setState({ _hasHydrated: true });
        }
      },
    }
  )
);

// Selector for convenience if needed, e.g. to get only a subset of the store or computed values
// export const useUserData = () => useLocalStorageStore(state => state.userData);

export default useLocalStorageStore;
